/**
 * Loyalty earn math — the ONE rule for how many points an order earns.
 *
 * Lives in `common` because both planes need it: the API's accrual and the
 * receipt-claim service award points with it (via `@order-up/core`'s
 * `loyaltyProgramPolicy`, which re-exports it), and the storefront cart
 * promises "Earn N points" with it. A second copy is how a cart promises
 * points the program never awards — the cart used to assume 10 points per
 * dollar whatever the program said.
 */

import type { ILoyaltyProgram, LoyaltyEarnRule } from '@order-up/models';

/**
 * Compute the integer earn points for an order under a program's earn rule.
 * Pure. Returns 0 when the program is inactive, the order is below the
 * minimum-spend gate, or the rule yields <= 0 points.
 */
export function computeEarnPoints(
  program: Pick<ILoyaltyProgram, 'status' | 'earnRule'>,
  eligibleSpendCents: number,
): number {
  if (program.status !== 'active') return 0;
  return computeEarnPointsForRule(program.earnRule, eligibleSpendCents);
}

/**
 * The same math for a rule the caller already knows is live — the storefront
 * receives only an ACTIVE program's rule (the slug payload's
 * `customerLoyaltyEarnRule`), never the program itself.
 */
export function computeEarnPointsForRule(
  earnRule: LoyaltyEarnRule,
  eligibleSpendCents: number,
): number {
  const minimumSpendCents = earnRule.minimumSpendCents ?? 0;
  if (eligibleSpendCents < minimumSpendCents) return 0;
  const rate = earnRule.pointsPerCurrencyUnit;
  if (!rate || rate <= 0) return 0;
  const points = Math.floor((eligibleSpendCents / 100) * rate);
  return points > 0 ? points : 0;
}

/**
 * Read an earn rule from an untrusted payload (the public slug response or a
 * page seed). Anything malformed is `null` — the cart then shows no points
 * line rather than a guessed number.
 */
export function readLoyaltyEarnRule(raw: unknown): LoyaltyEarnRule | null {
  if (!raw || typeof raw !== 'object') return null;
  const { pointsPerCurrencyUnit, minimumSpendCents } = raw as Record<string, unknown>;
  if (typeof pointsPerCurrencyUnit !== 'number' || !Number.isFinite(pointsPerCurrencyUnit) || pointsPerCurrencyUnit <= 0) {
    return null;
  }
  if (minimumSpendCents === undefined || minimumSpendCents === null) {
    return { pointsPerCurrencyUnit };
  }
  if (typeof minimumSpendCents !== 'number' || !Number.isInteger(minimumSpendCents) || minimumSpendCents < 0) {
    return null;
  }
  return { pointsPerCurrencyUnit, minimumSpendCents };
}
